import { GameState, getCurrentPlayer, TurnPhase } from './GameState';
import { Card, isWild, sortHand } from '../utils/CardUtils';
import { Meld, validateMeld, addToMeld, nextMeldId, isBurraco } from './Meld';
import {
  canDrawFromDeck,
  canDrawFromDiscard,
  canPlayMeld,
  canAddCardToMeld,
  canDiscardCard,
} from './GameRules';
import { calculateRoundScore } from './Scoring';
import { dealCards } from './Deck';
import { WINNING_SCORE } from '../utils/Constants';
import { resetMeldCounter } from './Meld';

export type GameAction =
  | { type: 'DRAW_DECK' }
  | { type: 'DRAW_DISCARD' }
  | { type: 'PLAY_MELD'; cardIds: string[] }
  | { type: 'ADD_TO_MELD'; cardId: string; meldId: string }
  | { type: 'DISCARD'; cardId: string }
  | { type: 'NEW_ROUND' }
  | { type: 'NEW_GAME' };

export interface ActionResult {
  success: boolean;
  state: GameState;
  message?: string;
  events: GameEvent[];
}

export type GameEvent =
  | { type: 'CARD_DRAWN'; source: 'deck' | 'discard'; playerId: string }
  | { type: 'MELD_PLAYED'; meld: Meld; playerId: string }
  | { type: 'CARD_ADDED_TO_MELD'; card: Card; meldId: string; playerId: string }
  | { type: 'CARD_DISCARDED'; card: Card; playerId: string }
  | { type: 'POZZETTO_TAKEN'; playerId: string; inFlight: boolean }
  | { type: 'GOING_OUT'; playerId: string }
  | { type: 'ROUND_END'; reason: string }
  | { type: 'GAME_OVER'; winnerId: string }
  | { type: 'TURN_CHANGED'; playerId: string };

function cloneState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state));
}

export function executeAction(state: GameState, action: GameAction): ActionResult {
  const newState = cloneState(state);
  const events: GameEvent[] = [];

  switch (action.type) {
    case 'DRAW_DECK':
      return drawFromDeck(newState, events);
    case 'DRAW_DISCARD':
      return drawFromDiscard(newState, events);
    case 'PLAY_MELD':
      return playMeld(newState, action.cardIds, events);
    case 'ADD_TO_MELD':
      return addCardToMeld(newState, action.cardId, action.meldId, events);
    case 'DISCARD':
      return discard(newState, action.cardId, events);
    case 'NEW_ROUND':
      return startNewRound(newState, events);
    case 'NEW_GAME':
      return startNewGame(events);
    default:
      return { success: false, state, message: 'Unknown action', events: [] };
  }
}

function drawFromDeck(state: GameState, events: GameEvent[]): ActionResult {
  if (!canDrawFromDeck(state)) {
    return { success: false, state, message: 'Cannot draw from deck', events: [] };
  }

  const player = getCurrentPlayer(state);
  const card = state.drawPile.pop()!;
  player.hand.push(card);
  player.hand = sortHand(player.hand);

  state.hasDrawnThisTurn = true;
  state.turnPhase = 'meld';
  state.stalemateTurnCount = 0;

  events.push({ type: 'CARD_DRAWN', source: 'deck', playerId: player.id });

  return { success: true, state, events };
}

function drawFromDiscard(state: GameState, events: GameEvent[]): ActionResult {
  if (!canDrawFromDiscard(state)) {
    return { success: false, state, message: 'Cannot draw from discard', events: [] };
  }

  const player = getCurrentPlayer(state);
  const discardCards = state.discardPile.splice(0, state.discardPile.length);
  player.hand.push(...discardCards);
  player.hand = sortHand(player.hand);

  state.hasDrawnThisTurn = true;
  state.turnPhase = 'meld';

  // Check stalemate: if discard pile had only 1 card
  if (discardCards.length === 1) {
    state.stalemateTurnCount++;
  } else {
    state.stalemateTurnCount = 0;
  }

  events.push({ type: 'CARD_DRAWN', source: 'discard', playerId: player.id });

  return { success: true, state, events };
}

function playMeld(state: GameState, cardIds: string[], events: GameEvent[]): ActionResult {
  if (!canPlayMeld(state, cardIds)) {
    return { success: false, state, message: 'Invalid meld', events: [] };
  }

  const player = getCurrentPlayer(state);
  const cards = cardIds.map(id => player.hand.find(c => c.id === id)!);
  const meldType = validateMeld(cards)!;

  const meld: Meld = {
    id: nextMeldId(),
    type: meldType,
    cards,
    playerId: player.id,
  };

  player.melds.push(meld);
  player.hand = player.hand.filter(c => !cardIds.includes(c.id));
  state.hasMeldedThisTurn = true;

  events.push({ type: 'MELD_PLAYED', meld, playerId: player.id });

  // Check if hand is empty — pozzetto pickup
  checkEmptyHand(state, player, events);

  return { success: true, state, events };
}

function addCardToMeld(state: GameState, cardId: string, meldId: string, events: GameEvent[]): ActionResult {
  if (!canAddCardToMeld(state, cardId, meldId)) {
    return { success: false, state, message: 'Cannot add card to meld', events: [] };
  }

  const player = getCurrentPlayer(state);
  const card = player.hand.find(c => c.id === cardId)!;
  const meldIndex = player.melds.findIndex(m => m.id === meldId);
  const updatedMeld = addToMeld(player.melds[meldIndex], card);

  if (!updatedMeld) {
    return { success: false, state, message: 'Failed to add card', events: [] };
  }

  player.melds[meldIndex] = updatedMeld;
  player.hand = player.hand.filter(c => c.id !== cardId);
  state.hasMeldedThisTurn = true;

  events.push({ type: 'CARD_ADDED_TO_MELD', card, meldId, playerId: player.id });

  // Check if hand is empty — pozzetto pickup
  checkEmptyHand(state, player, events);

  return { success: true, state, events };
}

function checkEmptyHand(state: GameState, player: typeof state.players[0], events: GameEvent[]): void {
  if (player.hand.length > 0) return;
  if (player.tookPozzetto) return; // Already took it

  // Find available pozzetto
  const pozzettoIndex = state.pozzetti[0] !== null ? 0 : (state.pozzetti[1] !== null ? 1 : -1);
  if (pozzettoIndex === -1) return;

  const pozzetto = state.pozzetti[pozzettoIndex]!;
  player.hand = sortHand(pozzetto);
  state.pozzetti[pozzettoIndex] = null;
  player.tookPozzetto = true;

  // In-flight: melded all cards without discarding, continues turn
  events.push({ type: 'POZZETTO_TAKEN', playerId: player.id, inFlight: true });
}

function discard(state: GameState, cardId: string, events: GameEvent[]): ActionResult {
  const player = getCurrentPlayer(state);
  const card = player.hand.find(c => c.id === cardId);
  if (!card) {
    return { success: false, state, message: 'Card not in hand', events: [] };
  }

  if (!canDiscardCard(state, card)) {
    return { success: false, state, message: 'Cannot discard this card', events: [] };
  }

  // Check if this is going out
  const goingOut = player.hand.length === 1 && player.tookPozzetto && player.melds.some(m => isBurraco(m)) && !isWild(card);

  player.hand = player.hand.filter(c => c.id !== cardId);
  state.discardPile.push(card);

  events.push({ type: 'CARD_DISCARDED', card, playerId: player.id });

  if (goingOut) {
    events.push({ type: 'GOING_OUT', playerId: player.id });
    return endRound(state, 'going_out', events);
  }

  // If hand is now empty and hasn't taken pozzetto, take it (with discard — wait until next turn)
  if (player.hand.length === 0 && !player.tookPozzetto) {
    const pozzettoIndex = state.pozzetti[0] !== null ? 0 : (state.pozzetti[1] !== null ? 1 : -1);
    if (pozzettoIndex !== -1) {
      const pozzetto = state.pozzetti[pozzettoIndex]!;
      player.hand = sortHand(pozzetto);
      state.pozzetti[pozzettoIndex] = null;
      player.tookPozzetto = true;
      events.push({ type: 'POZZETTO_TAKEN', playerId: player.id, inFlight: false });
    }
  }

  // Check deck empty condition
  if (state.drawPile.length <= 2) {
    return endRound(state, 'deck_empty', events);
  }

  // Check stalemate
  if (state.stalemateTurnCount >= 4) {
    return endRound(state, 'stalemate', events);
  }

  // Next turn
  state.currentPlayerIndex = 1 - state.currentPlayerIndex;
  state.turnPhase = 'draw';
  state.hasDrawnThisTurn = false;
  state.hasMeldedThisTurn = false;

  events.push({ type: 'TURN_CHANGED', playerId: getCurrentPlayer(state).id });

  return { success: true, state, events };
}

function endRound(state: GameState, reason: string, events: GameEvent[]): ActionResult {
  events.push({ type: 'ROUND_END', reason });

  const playerWentOut = reason === 'going_out' ? state.currentPlayerIndex : -1;

  for (let i = 0; i < 2; i++) {
    const player = state.players[i];
    const score = calculateRoundScore(
      player.melds,
      player.hand,
      i === playerWentOut,
      player.tookPozzetto,
    );
    player.totalScore += score.total;
  }

  state.roundScores.push({
    player0: state.players[0].totalScore,
    player1: state.players[1].totalScore,
  });

  // Check for game over
  if (state.players[0].totalScore >= WINNING_SCORE || state.players[1].totalScore >= WINNING_SCORE) {
    const winnerId = state.players[0].totalScore >= state.players[1].totalScore
      ? state.players[0].id
      : state.players[1].id;
    state.winner = winnerId;
    state.turnPhase = 'game_over';
    events.push({ type: 'GAME_OVER', winnerId });
  } else {
    state.turnPhase = 'round_end';
  }

  return { success: true, state, events };
}

function startNewRound(state: GameState, events: GameEvent[]): ActionResult {
  const deal = dealCards();
  resetMeldCounter();

  state.players[0].hand = sortHand(deal.hands[0]);
  state.players[0].melds = [];
  state.players[0].tookPozzetto = false;

  state.players[1].hand = sortHand(deal.hands[1]);
  state.players[1].melds = [];
  state.players[1].tookPozzetto = false;

  state.drawPile = deal.drawPile;
  state.discardPile = deal.discardPile;
  state.pozzetti = [deal.pozzetti[0], deal.pozzetti[1]];
  state.currentPlayerIndex = state.roundNumber % 2; // Alternate who goes first
  state.turnPhase = 'draw';
  state.roundNumber++;
  state.stalemateTurnCount = 0;
  state.hasDrawnThisTurn = false;
  state.hasMeldedThisTurn = false;

  return { success: true, state, events };
}

function startNewGame(events: GameEvent[]): ActionResult {
  const deal = dealCards();
  resetMeldCounter();

  const state: GameState = {
    players: [
      {
        id: 'human',
        hand: sortHand(deal.hands[0]),
        melds: [],
        tookPozzetto: false,
        totalScore: 0,
      },
      {
        id: 'ai',
        hand: sortHand(deal.hands[1]),
        melds: [],
        tookPozzetto: false,
        totalScore: 0,
      },
    ],
    drawPile: deal.drawPile,
    discardPile: deal.discardPile,
    pozzetti: [deal.pozzetti[0], deal.pozzetti[1]],
    currentPlayerIndex: 0,
    turnPhase: 'draw',
    roundNumber: 1,
    stalemateTurnCount: 0,
    hasDrawnThisTurn: false,
    hasMeldedThisTurn: false,
    roundScores: [],
    winner: null,
  };

  return { success: true, state, events };
}
